import { buildClientAppPlanFromConsultation } from "./clientAppMapper";
import {
  buildClientAppPackageContext,
  parseClientAppPackage,
  prepareClientAppPackageDraft,
} from "./clientAppPackageWorkflow";
import { collectFollowupSafetyConfirmationsV1 } from "./followupSafetySnapshot";
import { sha256HexSync } from "./sha256Sync";
import { mealKey } from "./extractMealsFromPlan";
import { readStructuredRecipeIngredients } from "./structuredRecipe";
import { redactDirectIdentifiers } from "./externalPlanWorkflow";
import {
  buildModularMealPrepEditorialDraft,
  buildPublishedModularMealPrep,
  explainPublishedModularMealPrepIssue,
} from "./mealPrep";
import { buildWeekMenusModel, weekMenuSlotLabel, weekMenusFoodConflicts } from "./weekMenus";
import { explicitFoodExclusions, findMenuFoodConflicts, sameExplicitFood } from "./explicitFoodGuard";
import { assertNoAiMention, identityNamesOf, NO_SELF_MENTION_INSTRUCTION } from "./aiMentionGuard";

export { explicitFoodExclusions, sameExplicitFood };

export const CLIENT_APP_WORKSPACE_SCHEMA = "anissa_client_app_workspace_lot";
export const CLIENT_APP_WORKSPACE_VERSION = 2;

const CLIENT_APP_WORKSPACE_NARRATIVE_MAX_LENGTH = 50_000;

export const CLIENT_APP_WORKSPACE_LOTS = Object.freeze([
  Object.freeze({ id: "lot_1", label: "Introduction et stratégie", sections: ["introduction", "strategy"] }),
  Object.freeze({ id: "lot_2", label: "Menus et recettes", sections: ["week"] }),
  Object.freeze({ id: "lot_3", label: "Cycle et Meal Prep", sections: ["rotation", "meal_prep"] }),
  Object.freeze({ id: "lot_4", label: "Frigo et compléments", sections: ["fridge", "supplements"] }),
]);

export const CLIENT_APP_WORKSPACE_SECTIONS = Object.freeze([
  Object.freeze({ id: "introduction", label: "Introduction", lotId: "lot_1" }),
  Object.freeze({ id: "strategy", label: "Stratégie", lotId: "lot_1" }),
  Object.freeze({ id: "week", label: "Semaine, menus et recettes", lotId: "lot_2" }),
  Object.freeze({ id: "rotation", label: "Rotation 4 semaines", lotId: "lot_3" }),
  Object.freeze({ id: "meal_prep", label: "Meal Prep", lotId: "lot_3" }),
  Object.freeze({ id: "fridge", label: "Frigo et courses", lotId: "lot_4" }),
  Object.freeze({ id: "supplements", label: "Compléments", lotId: "lot_4" }),
]);

const LOT_IDS = new Set(CLIENT_APP_WORKSPACE_LOTS.map((lot) => lot.id));
const SECTION_IDS = new Set(CLIENT_APP_WORKSPACE_SECTIONS.map((section) => section.id));
const REVIEW_STATUSES = new Set(["pending", "to_review", "reviewed"]);
const LOT_STATUSES = new Set(["ready", "needs_anissa_validation"]);
const ALLOWED_DAYS = new Set([
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
]);

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.keys(value).sort().map((key) => [key, stableValue(value[key])]));
}

function activeWorkspaceProjection(consultation) {
  const overrides = consultation?.editorial_overrides || {};
  return stableValue({
    introduction: consultation?.intro_letter || null,
    strategy: overrides.strategy || null,
    meals: overrides.meals || null,
    recipes: consultation?.meal_recipes || null,
    rotation: overrides.four_week_rotation || null,
    meal_prep: overrides.meal_prep_v2 || null,
    fridge: overrides.fridge || null,
    supplements_intro: overrides.supplements_intro || null,
  });
}

function activeWorkspaceSha256(consultation) {
  return sha256HexSync(JSON.stringify(activeWorkspaceProjection(consultation)));
}

export const MEALS_AUTHORITY_SOURCES = Object.freeze({
  LOT_2: "lot_2",
  WEEK_EDITOR: "week_editor",
});

/** Empreinte des menus actifs : réservoirs, recettes et rotation tels qu'enregistrés. */
export function weekEditorContentSha256(consultation) {
  const overrides = consultation?.editorial_overrides || {};
  return sha256HexSync(JSON.stringify(stableValue({
    meals: overrides.meals || null,
    recipes: consultation?.meal_recipes || null,
    rotation: overrides.four_week_rotation || null,
  })));
}

const WEEK_EDITOR_SECTIONS = ["week", "rotation", "meal_prep", "fridge"];

/**
 * Après un enregistrement de l'éditeur « Semaine, menus & recettes » : les
 * menus actifs deviennent la source du dossier (ils ne seront plus écrasés
 * par le lot Menus et recettes), et les sections concernées repassent en
 * relecture. Les autres sections ne bougent pas. Sans dossier, rien à faire.
 */
export function markClientAppWorkspaceWeekEditorSave(consultation, {
  now = new Date().toISOString(),
  sectionIds = WEEK_EDITOR_SECTIONS,
} = {}) {
  const workspace = consultation?.editorial_overrides?.client_app_workspace;
  if (!workspace || typeof workspace !== "object" || workspace.version !== CLIENT_APP_WORKSPACE_VERSION) {
    return consultation;
  }
  const contentSha256 = weekEditorContentSha256(consultation);
  // Rien n'a changé dans les menus : aucune section n'est remise en relecture.
  if (workspace.meals_authority?.source === MEALS_AUTHORITY_SOURCES.WEEK_EDITOR
      && workspace.meals_authority.content_sha256 === contentSha256) {
    return consultation;
  }
  const sections = { ...(workspace.sections || {}) };
  for (const sectionId of sectionIds) {
    const section = sections[sectionId];
    // Une section jamais importée reste « pending » : il n'y a rien à relire.
    if (section && section.status !== "pending") {
      sections[sectionId] = { ...section, status: "to_review", reviewed_at: null };
    }
  }
  return {
    ...consultation,
    editorial_overrides: {
      ...consultation.editorial_overrides,
      client_app_workspace: {
        ...touchWorkspace(workspace, { sections }),
        updated_at: now,
        meals_authority: {
          source: MEALS_AUTHORITY_SOURCES.WEEK_EDITOR,
          content_sha256: contentSha256,
          fridge_candidates: weekEditorFridgeCandidates(consultation),
          updated_at: now,
        },
      },
    },
  };
}

/**
 * Les menus actifs sont-ils exactement ceux du dernier dossier appliqué ?
 * Sert à n'afficher « Déjà publié » que pour le contenu réellement appliqué.
 */
export function menusMatchAppliedContent(consultation) {
  const workspace = consultation?.editorial_overrides?.client_app_workspace;
  if (!workspace || workspace.status !== "applied") return false;
  if (/^[0-9a-f]{64}$/.test(workspace.applied_meals_sha256 || "")) {
    return workspace.applied_meals_sha256 === weekEditorContentSha256(consultation);
  }
  // Dossiers appliqués avant l'empreinte dédiée : tout le contenu doit concorder.
  return /^[0-9a-f]{64}$/.test(workspace.applied_content_sha256 || "")
    && workspace.applied_content_sha256 === activeWorkspaceSha256(consultation);
}

export const WEEK_EDITOR_SOURCE_RETURN_NOTE ="Les menus de l’éditeur redeviennent la source";

/**
 * Anissa avait explicitement choisi le lot Menus et recettes : enregistrer
 * dans l'éditeur Semaine refait de l'éditeur la source. On le dit.
 */
export function weekEditorSaveReclaimsSource(consultation) {
  const authority = consultation?.editorial_overrides?.client_app_workspace?.meals_authority;
  return authority?.source === MEALS_AUTHORITY_SOURCES.LOT_2 && authority.chosen_explicitly === true;
}

/** Empreinte enregistrée périmée : les menus ont été modifiés hors de l'éditeur. */
export function isWeekEditorAuthorityStale(consultation) {
  const authority = consultation?.editorial_overrides?.client_app_workspace?.meals_authority;
  return authority?.source === MEALS_AUTHORITY_SOURCES.WEEK_EDITOR
    && authority.content_sha256 !== weekEditorContentSha256(consultation);
}

/**
 * Choix explicite d'Anissa de la source des menus du dossier (création du
 * dossier après des modifications dans l'éditeur Semaine).
 */
export function chooseClientAppWorkspaceMealsSource(workspace, consultation, source, { now = new Date().toISOString() } = {}) {
  if (source === MEALS_AUTHORITY_SOURCES.WEEK_EDITOR) {
    return touchWorkspace(workspace, {
      meals_authority: {
        source,
        content_sha256: weekEditorContentSha256(consultation),
        fridge_candidates: weekEditorFridgeCandidates(consultation),
        updated_at: now,
      },
    });
  }
  if (source === MEALS_AUTHORITY_SOURCES.LOT_2) {
    return touchWorkspace(workspace, { meals_authority: { source, updated_at: now, chosen_explicitly: true } });
  }
  throw new Error("Source des menus inconnue.");
}

/** Des menus ont-ils été enregistrés dans l'éditeur Semaine pour cette consultation ? */
export function hasWeekEditorMeals(consultation) {
  const overrides = consultation?.editorial_overrides || {};
  return Array.isArray(overrides.meals)
    && overrides.meals.some((entry) => entry?.edited_in === "week_editor");
}

/**
 * Une rotation enregistrée depuis sa carte garde l'empreinte des menus à
 * jour quand l'éditeur est déjà la source. Sinon, rien ne change.
 */
export function refreshClientAppWorkspaceWeekEditorAuthority(consultation, { now = new Date().toISOString() } = {}) {
  const workspace = consultation?.editorial_overrides?.client_app_workspace;
  if (workspace?.meals_authority?.source !== MEALS_AUTHORITY_SOURCES.WEEK_EDITOR) return consultation;
  // Une rotation ne touche ni les menus ni le frigo : seules la rotation et
  // le Meal Prep (calculé sur les 4 semaines) repassent en relecture.
  return markClientAppWorkspaceWeekEditorSave(consultation, { now, sectionIds: ["rotation", "meal_prep"] });
}

export const LOT_2_SOURCE_CONFIRMATION = "Les menus actifs viennent de l’éditeur Semaine. Choisis explicitement s’ils doivent être remplacés par ce lot ou conservés.";

/**
 * Importer ou corriger le lot Menus et recettes alors que les menus de
 * l'éditeur Semaine font foi exige un choix explicite d'Anissa :
 * `mealsSource` = "lot_2" (remplacer) ou "week_editor" (conserver).
 */
function lot2MealsAuthorityPatch(workspace, lotId, mealsSource) {
  if (lotId !== "lot_2") return {};
  const current = workspace?.meals_authority;
  if (current?.source === MEALS_AUTHORITY_SOURCES.WEEK_EDITOR) {
    if (mealsSource === MEALS_AUTHORITY_SOURCES.WEEK_EDITOR) return {};
    if (mealsSource !== MEALS_AUTHORITY_SOURCES.LOT_2) {
      const error = new Error(LOT_2_SOURCE_CONFIRMATION);
      error.code = "meals_source_confirmation_required";
      throw error;
    }
  }
  return {
    meals_authority: {
      source: MEALS_AUTHORITY_SOURCES.LOT_2,
      updated_at: new Date().toISOString(),
      // Anissa a choisi le lot contre les menus de l'éditeur : un futur
      // enregistrement dans l'éditeur le lui signalera, jamais en silence.
      ...(mealsSource === MEALS_AUTHORITY_SOURCES.LOT_2 ? { chosen_explicitly: true } : {}),
    },
  };
}

function cleanText(value, maxLength, { optional = false } = {}) {
  if (value === undefined && optional) return "";
  if (typeof value !== "string") return null;
  const text = value.replace(/\s+/g, " ").trim();
  if (!text) return optional ? "" : null;
  return text.length <= maxLength ? text : null;
}

function hasExactKeys(value, keys) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return JSON.stringify(actual) === JSON.stringify(expected);
}

function cleanStringArray(value, { min = 0, max = 20, maxLength = 240 } = {}) {
  if (!Array.isArray(value) || value.length < min || value.length > max) return null;
  const cleaned = value.map((item) => cleanText(item, maxLength));
  if (cleaned.some((item) => !item)) return null;
  const fingerprints = cleaned.map((item) => item.toLocaleLowerCase("fr"));
  return new Set(fingerprints).size === fingerprints.length ? cleaned : null;
}

function baselineConsultation(consultation) {
  const overrides = { ...(consultation?.editorial_overrides || {}) };
  [
    "strategy", "meals", "four_week_rotation", "meal_prep", "meal_prep_v2", "fridge",
    "fridge_sync", "supplements_intro", "client_app_package", "client_app_workspace",
  ].forEach((key) => delete overrides[key]);
  return {
    ...consultation,
    intro_letter: null,
    meal_recipes: {},
    editorial_overrides: overrides,
  };
}

function flattenFridgeLabels(fridge) {
  const labels = [];
  const add = (value) => {
    const text = cleanText(value, 180);
    if (text) labels.push(text);
  };
  (Array.isArray(fridge?.essentials) ? fridge.essentials : []).forEach((item) => add(item?.label));
  ["favorite", "limit"].forEach((key) => {
    (Array.isArray(fridge?.[key]) ? fridge[key] : []).forEach((category) => {
      (Array.isArray(category?.items) ? category.items : []).forEach((item) => add(item?.label));
    });
  });
  return [...new Map(labels.map((label) => [label.toLocaleLowerCase("fr"), label])).values()];
}

function supplementSource(plan) {
  return (Array.isArray(plan?.sections?.protocols_data?.groups)
    ? plan.sections.protocols_data.groups
    : []).flatMap((group) => (Array.isArray(group?.items) ? group.items : []).map((item) => ({
    id: String(item?.id || ""),
    name: String(item?.name || "").trim(),
    dose: String(item?.dose || "").trim(),
    timing_detail: String(item?.timing_detail || "").trim(),
  }))).filter((item) => item.id && item.name);
}

/**
 * Repères frigo issus des menus de l'éditeur Semaine : ingrédients des
 * recettes de chaque réservoir enregistré (lignes ou version structurée).
 */
export function weekEditorFridgeCandidates(consultation) {
  const overrides = consultation?.editorial_overrides || {};
  const recipes = consultation?.meal_recipes || {};
  const labels = [];
  for (const entry of Array.isArray(overrides.meals) ? overrides.meals : []) {
    const slot = String(entry?.slot || "").trim();
    const titles = [entry?.title, ...(Array.isArray(entry?.alternatives) ? entry.alternatives.map((alt) => alt?.title) : [])];
    for (const title of titles) {
      if (!slot || typeof title !== "string" || !title.trim()) continue;
      for (const ingredient of readStructuredRecipeIngredients(recipes[mealKey(slot, title)]) || []) {
        const label = cleanText(ingredient?.label, 180);
        if (label) labels.push(label);
      }
    }
  }
  return [...new Map(labels.map((label) => [label.toLocaleLowerCase("fr"), label])).values()];
}

function workspaceFridgeCandidates(context, workspace) {
  const labels = [...(context?.fridgeCandidates || [])];
  // Menus de l'éditeur Semaine : le lot Menus et recettes ne fait plus foi,
  // les repères viennent des menus enregistrés (figés avec leur empreinte).
  if (workspace?.meals_authority?.source === MEALS_AUTHORITY_SOURCES.WEEK_EDITOR) {
    labels.push(...(Array.isArray(workspace.meals_authority.fridge_candidates)
      ? workspace.meals_authority.fridge_candidates
      : []));
    return [...new Map(labels.map((label) => [label.toLocaleLowerCase("fr"), label])).values()];
  }
  const meals = workspace?.lots?.lot_2?.content?.meals;
  const addRecipe = (recipe) => {
    (Array.isArray(recipe?.ingredients_structured) ? recipe.ingredients_structured : [])
      .forEach((ingredient) => {
        const label = cleanText(ingredient?.label, 180);
        if (label) labels.push(label);
      });
  };
  (Array.isArray(meals) ? meals : []).forEach((meal) => {
    addRecipe(meal?.primary?.recipe);
    (Array.isArray(meal?.alternatives) ? meal.alternatives : [])
      .forEach((alternative) => addRecipe(alternative?.recipe));
  });
  return [...new Map(labels.map((label) => [label.toLocaleLowerCase("fr"), label])).values()];
}

function contextWithWorkspace(context, workspace) {
  return {
    ...context,
    fridgeCandidates: workspaceFridgeCandidates(context, workspace),
  };
}

function lotEndMarker(sourceRevision, lotId) {
  return `ANISSA-${lotId.toUpperCase()}-${sha256HexSync(`${sourceRevision}:${lotId}`).slice(0, 20)}`;
}

export function buildClientAppWorkspaceContext({ client, consultation, directives = "" }) {
  if (!client?.id || !consultation?.id) throw new Error("La version du programme est indisponible.");
  const sourceConsultation = baselineConsultation(consultation);
  const sourcePlan = buildClientAppPlanFromConsultation(client, sourceConsultation);
  const packageContext = buildClientAppPackageContext({
    client,
    consultation: sourceConsultation,
    weekMeals: sourcePlan.sections.week_meals,
    directives,
  });
  const identity = {
    prenom: client?.prenom,
    nom: client?.nom || client?.form?.nom,
    email: client?.email || client?.form?.email,
  };
  const narrativeSource = redactDirectIdentifiers(
    String(sourceConsultation?.nutrition_plan || sourceConsultation?.nutritionPlan || ""),
    identity,
    { maxLength: CLIENT_APP_WORKSPACE_NARRATIVE_MAX_LENGTH, failOnTruncation: true },
  );
  const narrativeSourceSha256 = sha256HexSync(narrativeSource);
  const narrativeSourceLength = narrativeSource.length;
  const narrativeSourceEndMarker = `FIN_PROGRAMME_AUTORITATIF_${narrativeSourceSha256}`;
  const supplements = supplementSource(sourcePlan);
  const fridgeCandidates = flattenFridgeLabels(sourcePlan.sections.fridge_data);
  const source = stableValue({
    version: CLIENT_APP_WORKSPACE_VERSION,
    package_source_revision: packageContext.sourceRevision,
    nutrition_plan: String(sourceConsultation?.nutrition_plan || sourceConsultation?.nutritionPlan || ""),
    supplements,
    fiche_frigo_json: sourceConsultation?.fiche_frigo_json || sourceConsultation?.ficheFrigoJson || null,
  });
  const sourceRevision = `client-app-workspace-v2-${sha256HexSync(JSON.stringify(source))}`;
  return Object.freeze({
    sourceRevision,
    packageContext,
    sourcePlan,
    directives,
    narrativeSource,
    narrativeSourceSha256,
    narrativeSourceLength,
    narrativeSourceEndMarker,
    fridgeCandidates: Object.freeze(fridgeCandidates),
    supplementItems: Object.freeze(supplements),
    supplementItemsSha256: sha256HexSync(JSON.stringify(stableValue(supplements))),
    endMarkers: Object.freeze(Object.fromEntries(
      CLIENT_APP_WORKSPACE_LOTS.map((lot) => [lot.id, lotEndMarker(sourceRevision, lot.id)]),
    )),
    // Prénom et nom retirés avant le contrôle « outil ou IA » (« Bonjour Claude »).
    identityNames: Object.freeze(identityNamesOf(client)),
    // Points signalés sans rien empêcher : Anissa les voit et décide.
    safetyConfirmations: collectFollowupSafetyConfirmationsV1(client.form || {}),
  });
}

function emptySections() {
  return Object.fromEntries(CLIENT_APP_WORKSPACE_SECTIONS.map((section) => [section.id, {
    status: "pending",
    lot_id: section.lotId,
    reviewed_at: null,
  }]));
}

export function createClientAppWorkspaceDraft(context, existing = null) {
  if (!context?.sourceRevision) throw new Error("Le dossier application ne peut pas être initialisé.");
  if (existing?.version === CLIENT_APP_WORKSPACE_VERSION
      && existing?.source_revision === context.sourceRevision) {
    return existing;
  }
  return {
    version: CLIENT_APP_WORKSPACE_VERSION,
    status: "draft",
    source_revision: context.sourceRevision,
    draft_revision: Number.isInteger(existing?.draft_revision) && existing.draft_revision >= 0
      ? existing.draft_revision
      : 0,
    updated_at: null,
    raw_by_lot: {},
    lots: {},
    sections: emptySections(),
  };
}

/** Fin de l'objet JSON équilibré qui commence en `start` (chaînes respectées), ou -1. */
function balancedObjectEnd(text, start) {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let index = start; index < text.length; index += 1) {
    const char = text[index];
    if (inString) {
      if (escaped) escaped = false;
      else if (char === "\\") escaped = true;
      else if (char === '"') inString = false;
      continue;
    }
    if (char === '"') inString = true;
    else if (char === "{") depth += 1;
    else if (char === "}") {
      depth -= 1;
      if (depth === 0) return index;
    }
  }
  return -1;
}

/**
 * Collage tolérant : le texte avant et après l'objet JSON (« Voici le lot : »,
 * « N'hésite pas… ») et les balises ``` sont retirés. Le contrôle strict du
 * lot s'applique ensuite à l'objet, sans rien de plus permissif.
 */
export function extractJsonObjectText(rawText) {
  const text = String(rawText ?? "").replace(/^﻿/, "").trim();
  // Chaque « { » est un départ possible : on garde le premier objet équilibré
  // qui se lit ET porte le schéma du dossier. Le texte autour (accolades
  // comprises) ne peut donc plus fausser l'extraction.
  let firstParsable = null;
  for (let start = text.indexOf("{"); start >= 0; start = text.indexOf("{", start + 1)) {
    const end = balancedObjectEnd(text, start);
    if (end < 0) continue;
    const candidate = text.slice(start, end + 1);
    let value;
    try {
      value = JSON.parse(candidate);
    } catch {
      continue;
    }
    if (value && typeof value === "object" && !Array.isArray(value)) {
      if (value.schema === CLIENT_APP_WORKSPACE_SCHEMA) return candidate;
      if (firstParsable === null) firstParsable = candidate;
    }
  }
  if (firstParsable !== null) return firstParsable;
  const first = text.indexOf("{");
  const last = text.lastIndexOf("}");
  return first >= 0 && last > first ? text.slice(first, last + 1) : text;
}

export const PASTED_FRAME_MESSAGE = "C’est le cadre que tu as collé : colle ici la réponse obtenue avec le cadre, qui commence par {";
const FRAME_MARKERS = Object.freeze(["FORMAT EXACT À REMPLIR", "PROGRAMME AUTORITATIF", "CONTRAINTES DE FORME"]);

/** Le texte collé est-il le cadre de préparation lui-même, et non la réponse ? */
export function looksLikePastedFrame(rawText) {
  const text = String(rawText ?? "");
  return FRAME_MARKERS.some((marker) => text.includes(marker));
}

function parseEnvelope(rawText, context, lotId) {
  if (!LOT_IDS.has(lotId) || typeof rawText !== "string" || rawText.trim().length < 40) {
    throw new Error("Colle le lot complet avant de le vérifier.");
  }
  if (looksLikePastedFrame(rawText)) throw new Error(PASTED_FRAME_MESSAGE);
  let value;
  try {
    value = JSON.parse(extractJsonObjectText(rawText));
  } catch {
    throw new Error("Le lot collé ne contient pas d’objet JSON lisible : recopie toute la réponse, de la première { à la dernière }.");
  }
  if (!hasExactKeys(value, ["schema", "version", "source_revision", "lot_id", "end_marker", "status", "issues", "content"])
      || value.schema !== CLIENT_APP_WORKSPACE_SCHEMA
      || value.version !== CLIENT_APP_WORKSPACE_VERSION
      || value.source_revision !== context.sourceRevision
      || value.lot_id !== lotId
      || value.end_marker !== context.endMarkers?.[lotId]
      || !LOT_STATUSES.has(value.status)) {
    throw new Error("Ce lot ne correspond pas à la version actuelle du dossier.");
  }
  const issues = cleanStringArray(value.issues, { min: value.status === "needs_anissa_validation" ? 1 : 0, max: 20, maxLength: 500 });
  if (!issues
      || (value.status === "ready" && (issues.length !== 0 || value.content == null))
      || (value.status === "needs_anissa_validation" && value.content !== null)) {
    throw new Error("Le statut de ce lot est incomplet ou incohérent.");
  }
  return { status: value.status, issues, content: value.content };
}

// Une seule source pour les limites du Lot 1 : les contrôles, le cadre de
// préparation et les champs de saisie d’Anissa lisent tous cet objet, donc ils
// ne peuvent pas se contredire.
export const LOT_1_LIMITS = Object.freeze({
  greeting: 120,
  body: Object.freeze({ min: 1, max: 8, maxLength: 900 }),
  pull_quote: 280,
  tailored_points: Object.freeze({ min: 0, max: 6, title: 120, detail: 420 }),
  signature: 80,
  subtitle: 240,
  essential: Object.freeze({ min: 1, max: 8, maxLength: 500 }),
  pillars: Object.freeze({ min: 1, max: 8, title: 120, description: 600 }),
  takeaways: Object.freeze({ min: 1, max: 8, maxLength: 300 }),
});

const FIELD_LABELS = Object.freeze({
  greeting: "la salutation (greeting)",
  body: "les paragraphes (body)",
  pull_quote: "la phrase mise en avant (pull_quote)",
  tailored_points: "les points personnalisés (tailored_points)",
  signature: "la signature (signature)",
  subtitle: "le sous-titre (subtitle)",
  essential: "les repères essentiels (essential)",
  pillars: "les piliers (pillars)",
  takeaways: "les points à retenir (takeaways)",
  title: "le titre (title)",
  detail: "le détail (detail)",
  description: "la description (description)",
});

function fieldLabel(key) {
  return FIELD_LABELS[key] || `le champ ${key}`;
}

function keyIssue(value, keys, blockLabel) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return `${blockLabel} : bloc absent ou mal formé.`;
  }
  const actual = Object.keys(value);
  const missing = keys.filter((key) => !actual.includes(key));
  if (missing.length > 0) {
    return `${blockLabel} : il manque ${missing.map(fieldLabel).join(", ")}.`;
  }
  const extra = actual.filter((key) => !keys.includes(key));
  if (extra.length > 0) {
    return `${blockLabel} : ${extra.join(", ")} en trop, aucun champ supplémentaire n’est accepté.`;
  }
  return null;
}

function requireText(value, maxLength, label, { optional = false } = {}) {
  const text = cleanText(value, maxLength, { optional });
  if (text !== null) return text;
  if (typeof value !== "string") throw new Error(`${label} : du texte est attendu.`);
  if (!value.trim()) throw new Error(`${label} : ne peut pas être vide.`);
  throw new Error(`${label} : ${value.replace(/\s+/g, " ").trim().length} caractères, ${maxLength} au maximum.`);
}

function requireTextList(value, { min, max, maxLength }, label) {
  if (!Array.isArray(value)) throw new Error(`${label} : une liste est attendue.`);
  if (value.length < min) throw new Error(`${label} : ${value.length} entrée(s), il en faut ${min} au minimum.`);
  if (value.length > max) throw new Error(`${label} : ${value.length} entrées, ${max} au maximum.`);
  value.forEach((item, index) => requireText(item, maxLength, `${label}, entrée ${index + 1}`));
  const cleaned = cleanStringArray(value, { min, max, maxLength });
  if (!cleaned) throw new Error(`${label} : deux entrées sont identiques, chacune doit être différente.`);
  return cleaned;
}

function requireObjectList(value, { min, max, keys, fields }, label) {
  if (!Array.isArray(value)) throw new Error(`${label} : une liste est attendue.`);
  if (value.length < min) throw new Error(`${label} : ${value.length} entrée(s), il en faut ${min} au minimum.`);
  if (value.length > max) throw new Error(`${label} : ${value.length} entrées, ${max} au maximum.`);
  return value.map((item, index) => {
    const itemLabel = `${label}, entrée ${index + 1}`;
    const issue = keyIssue(item, keys, itemLabel);
    if (issue) throw new Error(issue);
    return Object.fromEntries(keys.map((key) => [
      key,
      requireText(item[key], fields[key], `${itemLabel}, ${fieldLabel(key)}`),
    ]));
  });
}

function parseIntroduction(value) {
  const issue = keyIssue(value, ["greeting", "body", "pull_quote", "tailored_points", "signature"], "Introduction");
  if (issue) throw new Error(issue);
  return {
    greeting: requireText(value.greeting, LOT_1_LIMITS.greeting, `Introduction, ${fieldLabel("greeting")}`),
    body: requireTextList(value.body, LOT_1_LIMITS.body, `Introduction, ${fieldLabel("body")}`),
    pull_quote: requireText(value.pull_quote, LOT_1_LIMITS.pull_quote, `Introduction, ${fieldLabel("pull_quote")}`, { optional: true }),
    tailored_points: requireObjectList(value.tailored_points, {
      min: LOT_1_LIMITS.tailored_points.min,
      max: LOT_1_LIMITS.tailored_points.max,
      keys: ["title", "detail"],
      fields: { title: LOT_1_LIMITS.tailored_points.title, detail: LOT_1_LIMITS.tailored_points.detail },
    }, `Introduction, ${fieldLabel("tailored_points")}`),
    signature: requireText(value.signature, LOT_1_LIMITS.signature, `Introduction, ${fieldLabel("signature")}`),
  };
}

function parseStrategy(value) {
  const issue = keyIssue(value, ["subtitle", "essential", "pillars", "takeaways"], "Stratégie");
  if (issue) throw new Error(issue);
  return {
    subtitle: requireText(value.subtitle, LOT_1_LIMITS.subtitle, `Stratégie, ${fieldLabel("subtitle")}`),
    essential: requireTextList(value.essential, LOT_1_LIMITS.essential, `Stratégie, ${fieldLabel("essential")}`),
    pillars: requireObjectList(value.pillars, {
      min: LOT_1_LIMITS.pillars.min,
      max: LOT_1_LIMITS.pillars.max,
      keys: ["title", "description"],
      fields: { title: LOT_1_LIMITS.pillars.title, description: LOT_1_LIMITS.pillars.description },
    }, `Stratégie, ${fieldLabel("pillars")}`),
    takeaways: requireTextList(value.takeaways, LOT_1_LIMITS.takeaways, `Stratégie, ${fieldLabel("takeaways")}`),
  };
}

function parseLot1(content) {
  const issue = keyIssue(content, ["introduction", "strategy"], "Lot Introduction et stratégie");
  if (issue) throw new Error(issue);
  return { introduction: parseIntroduction(content.introduction), strategy: parseStrategy(content.strategy) };
}

function parseLot2(content, context) {
  if (!hasExactKeys(content, ["meals", "grammages_provenance"])
      || !hasExactKeys(content.grammages_provenance, ["source_revision", "status"])
      || content.grammages_provenance.source_revision !== context.packageContext.sourceRevision
      || content.grammages_provenance.status !== "proposed_for_anissa_review") {
    throw new Error("Le lot Menus et recettes est incomplet ou ses grammages ne sont pas reliés à la source courante.");
  }
  parseClientAppPackage(JSON.stringify({
    schema: "anissa_client_app_package",
    version: 1,
    source_revision: context.packageContext.sourceRevision,
    meals: content.meals,
    meal_prep: { people: 1, session_count: 2, preferred_days: ["sunday", "wednesday"] },
  }), context.packageContext);
  assertNoExplicitFoodConflict(content.meals, context.packageContext.safetySnapshot);
  return stableValue(content);
}

/**
 * Lot Menus et recettes : même contrôle que les menus de l'éditeur Semaine —
 * titre, description, ingrédients (lignes et structurés), étapes et consigne
 * de chaque choix. Le premier conflit bloque l'import comme l'application.
 */
function assertNoExplicitFoodConflict(meals, snapshot) {
  const choices = (meals || []).flatMap((meal) => {
    const slotLabel = weekMenuSlotLabel(meal?.slot);
    return [
      { ...(meal?.primary || {}), label: `${slotLabel}, plat principal « ${meal?.primary?.title || ""} »` },
      ...(meal?.alternatives || []).map((alternative, index) => ({
        ...alternative,
        label: `${slotLabel}, alternative ${index + 1} « ${alternative?.title || ""} »`,
      })),
    ];
  });
  const [conflict] = findMenuFoodConflicts(choices, snapshot);
  if (conflict) throw new Error(conflict.message);
}

export const WEEK_EDITOR_ROTATION_REQUIRED ="Rotation 4 semaines : choisis et valide les plats principaux des 4 semaines";

/**
 * Tout ce qui empêche d'appliquer les menus de l'éditeur Semaine. Rien n'est
 * complété ni validé à la place d'Anissa : chaque repas doit être validé, la
 * rotation choisie et validée par elle, et aucun aliment exclu ne passe.
 */
export function getWeekEditorApplyIssues({ client, consultation, snapshot = null }) {
  const model = buildWeekMenusModel({ client, consultation });
  if (model.slots.length === 0) {
    return ["Semaine, menus & recettes est vide : complète les repas avant d’appliquer."];
  }
  const issues = [];
  for (const slotModel of model.slots) {
    if (!slotModel.hasOverride) {
      issues.push(`${slotModel.label} : repas pas encore enregistré dans l’éditeur Semaine — enregistre-le pour couvrir les 7 jours`);
    } else if (slotModel.variesByDay) {
      issues.push(`${slotModel.label} : le repas varie encore selon les jours — enregistre-le dans l’éditeur Semaine`);
    }
    issues.push(...slotModel.issues);
    if (slotModel.issues.length === 0 && slotModel.status !== "validated") {
      issues.push(`${slotModel.label} : modifications à valider avant l’application`);
    }
  }
  if (snapshot) {
    // Seuls les conflits confirmés par Anissa (texte et exclusions exacts)
    // sont acceptés ; tous les autres bloquent l'application.
    for (const conflict of weekMenusFoodConflicts(model, snapshot, { consultation })) {
      if (!conflict.confirmed && !issues.includes(conflict.message)) issues.push(conflict.message);
    }
  }
  if (model.rotation.status !== "validated" || !model.rotation.reviewed) {
    issues.push(WEEK_EDITOR_ROTATION_REQUIRED);
  }
  return issues;
}

/**
 * Applique les menus actifs enregistrés par l'éditeur Semaine, sans les
 * remplacer par le lot Menus et recettes. La rotation est celle qu'Anissa a
 * validée, telle quelle : aucune répartition par défaut n'est appliquée.
 */
function buildWeekEditorAppliedConsultation({ client, consultation, context, mealPrepSettings }) {
  const issues = getWeekEditorApplyIssues({
    client,
    consultation,
    snapshot: context.packageContext.safetySnapshot,
  });
  if (issues.length > 0) {
    const error = new Error(`Avant l’application — ${issues[0]}.`);
    error.issues = issues;
    throw error;
  }
  const mapped = buildClientAppPlanFromConsultation(client, consultation).sections.week_meals;
  // Même règle que le chemin du lot Menus et recettes : le Meal Prep n'est
  // validé ici qu'après la relecture de sa section dans le dossier.
  // Décision de Benoit (17.10) : le Meal Prep est facultatif. S'il ne se
  // calcule pas avec ces recettes, le dossier s'applique quand même ; le Meal
  // Prep reste en brouillon et n'est pas publié (l'app ne l'affiche pas) ;
  // l'écran le signale à Anissa avec mealPrepNotApplied().
  const mealPrepDraft = buildModularMealPrepEditorialDraft(mapped, mealPrepSettings);
  const validated = { ...mealPrepDraft.override, status: "validated" };
  const publishable = mealPrepDraft.result?.ready && buildPublishedModularMealPrep(mapped, validated);
  const mealPrep = publishable ? validated : { ...mealPrepDraft.override, status: "draft" };
  return {
    ...consultation,
    editorial_overrides: { ...(consultation.editorial_overrides || {}), meal_prep_v2: mealPrep },
  };
}

/**
 * Après application : raison pour laquelle le Meal Prep n'est pas publié
 * (null s'il l'est). Le dossier, lui, est appliqué.
 */
export function mealPrepNotApplied(client, consultation) {
  const value = consultation?.editorial_overrides?.meal_prep_v2;
  if (!value || value.status === "validated") return null;
  const mapped = buildClientAppPlanFromConsultation(client, consultation).sections.week_meals;
  const draft = buildModularMealPrepEditorialDraft(mapped, value);
  if (!draft.result?.ready) return draft.result?.issues?.[0] || "les recettes ne permettent pas de calculer les préparations";
  return explainPublishedModularMealPrepIssue(mapped, { ...draft.override, status: "validated" })
    || "le calcul n’aboutit pas";
}

function parseLot3(content) {
  if (!hasExactKeys(content, ["rotation", "meal_prep"])
      || !hasExactKeys(content.rotation, ["mode"])
      || content.rotation.mode !== "four_distinct_weeks"
      || !hasExactKeys(content.meal_prep, ["people", "session_count", "preferred_days"])) {
    throw new Error("Le lot Cycle et Meal Prep est incomplet.");
  }
  const prep = content.meal_prep;
  if (!Number.isInteger(prep.people) || prep.people < 1 || prep.people > 20
      || !Number.isInteger(prep.session_count) || prep.session_count < 1 || prep.session_count > 3
      || !Array.isArray(prep.preferred_days) || prep.preferred_days.length !== prep.session_count
      || new Set(prep.preferred_days).size !== prep.preferred_days.length
      || !prep.preferred_days.every((day) => ALLOWED_DAYS.has(day))) {
    throw new Error("Les réglages du Meal Prep sont incomplets ou invalides.");
  }
  return {
    rotation: { mode: "four_distinct_weeks" },
    meal_prep: { people: prep.people, session_count: prep.session_count, preferred_days: [...prep.preferred_days] },
  };
}

function resolveCandidateList(values, candidates, label) {
  const cleaned = cleanStringArray(values, { min: label === "À privilégier" ? 1 : 0, max: 80, maxLength: 180 });
  if (!cleaned) throw new Error(`${label} : liste absente, répétée ou invalide.`);
  const byKey = new Map(candidates.map((item) => [item.toLocaleLowerCase("fr"), item]));
  const resolved = cleaned.map((item) => byKey.get(item.toLocaleLowerCase("fr")) || null);
  if (resolved.some((item) => !item)) {
    throw new Error(`${label} : un élément n’appartient pas aux repères déjà validés.`);
  }
  return resolved;
}

function parseLot4(content, context) {
  if (!hasExactKeys(content, ["fridge", "supplements"])
      || !hasExactKeys(content.fridge, ["to_favor", "to_limit"])
      || !hasExactKeys(content.supplements, ["intro", "source_items_sha256"])) {
    throw new Error("Le lot Frigo et compléments est incomplet.");
  }
  const intro = cleanText(content.supplements.intro, 900);
  const expectedIntro = context.supplementItems.length > 0
    ? "Retrouve ici les compléments et les moments de prise déjà validés avec Anissa."
    : "Aucun complément prévu dans ce programme.";
  if (intro !== expectedIntro
      || content.supplements.source_items_sha256 !== context.supplementItemsSha256) {
    throw new Error("La présentation des compléments ne correspond pas au protocole déjà validé.");
  }
  return {
    fridge: {
      to_favor: resolveCandidateList(content.fridge.to_favor, context.fridgeCandidates, "À privilégier"),
      to_limit: resolveCandidateList(content.fridge.to_limit, context.fridgeCandidates, "À limiter"),
    },
    supplements: { intro, source_items_sha256: context.supplementItemsSha256 },
  };
}

function parseLotContentStrict(content, context, lotId) {
  if (lotId === "lot_1") return parseLot1(content);
  if (lotId === "lot_2") return parseLot2(content, context);
  if (lotId === "lot_3") return parseLot3(content);
  if (lotId === "lot_4") return parseLot4(content, context);
  throw new Error("Lot inconnu.");
}

// Aucun lot (collé, rédigé par le cockpit ou corrigé) ne peut nommer un outil
// ou une IA : la cliente ne doit jamais le voir dans son app.
function parseLotContent(content, context, lotId) {
  const parsed = parseLotContentStrict(content, context, lotId);
  assertNoAiMention(parsed, CLIENT_APP_WORKSPACE_LOTS.find((lot) => lot.id === lotId)?.label || "Lot", {
    identityNames: context?.identityNames || [],
  });
  return parsed;
}

export function parseClientAppWorkspaceLot(rawText, context, lotId) {
  const envelope = parseEnvelope(rawText, context, lotId);
  if (envelope.status === "needs_anissa_validation") {
    throw new Error(`À valider Anissa — ${envelope.issues.join(" • ")}`);
  }
  return parseLotContent(envelope.content, context, lotId);
}

function parseClientAppWorkspaceLotResult(rawText, context, lotId) {
  const envelope = parseEnvelope(rawText, context, lotId);
  if (envelope.status === "needs_anissa_validation") return envelope;
  return { status: "ready", issues: [], content: parseClientAppWorkspaceLot(rawText, context, lotId) };
}

/**
 * Un lot enregistré passe-t-il encore le contrôle avec le contexte actuel ?
 * (Ex. le frigo après une modification des menus de l'éditeur Semaine.)
 * @returns {string|null} message du contrôle, ou null si le lot est valide
 */
export function getClientAppWorkspaceLotIssueDetail(context, workspace, lotId) {
  const lot = workspace?.lots?.[lotId];
  if (!lot || lot.status !== "ready") return null;
  try {
    parseLotContent(lot.content, contextWithWorkspace(context, workspace), lotId);
    return null;
  } catch (error) {
    return { code: error?.code || "invalid", message: error?.message || "Ce lot ne passe plus le contrôle." };
  }
}

export function getClientAppWorkspaceLotIssue(context, workspace, lotId) {
  return getClientAppWorkspaceLotIssueDetail(context, workspace, lotId)?.message || null;
}

function touchWorkspace(workspace, patch) {
  return {
    ...workspace,
    ...patch,
    status: "draft",
    draft_revision: Number(workspace?.draft_revision || 0) + 1,
    updated_at: new Date().toISOString(),
  };
}

export function importClientAppWorkspaceLot({ workspace, context, lotId, rawText, mealsSource }) {
  // Version du programme vérifiée AVANT de demander la source des menus :
  // inutile de faire choisir Anissa pour un lot qui sera refusé.
  if (workspace?.source_revision !== context?.sourceRevision) {
    throw new Error("Le programme a changé. Repars d’un nouveau dossier application.");
  }
  const mealsAuthorityPatch = lot2MealsAuthorityPatch(workspace, lotId, mealsSource);
  const result = parseClientAppWorkspaceLotResult(
    rawText,
    contextWithWorkspace(context, workspace),
    lotId,
  );
  const lot = CLIENT_APP_WORKSPACE_LOTS.find((item) => item.id === lotId);
  const sections = { ...workspace.sections };
  lot.sections.forEach((sectionId) => {
    sections[sectionId] = { status: "to_review", lot_id: lotId, reviewed_at: null };
  });
  const lots = {
    ...workspace.lots,
    [lotId]: { imported_at: new Date().toISOString(), ...result },
  };
  revokeDependentLots(lots, sections, lotId);
  return touchWorkspace(workspace, {
    lots,
    raw_by_lot: { ...(workspace.raw_by_lot || {}), [lotId]: "" },
    sections,
    ...mealsAuthorityPatch,
  });
}

function revokeDependentLots(lots, sections, lotId) {
  if (lotId !== "lot_2") return;
  delete lots.lot_3;
  delete lots.lot_4;
  ["rotation", "meal_prep", "fridge", "supplements"].forEach((sectionId) => {
    sections[sectionId] = {
      status: "pending",
      lot_id: CLIENT_APP_WORKSPACE_SECTIONS.find((section) => section.id === sectionId).lotId,
      reviewed_at: null,
    };
  });
}

export function editClientAppWorkspaceLotContent({ workspace, context, lotId, content, mealsSource }) {
  if (workspace?.source_revision !== context?.sourceRevision) {
    throw new Error("Le programme a changé. Repars d’un nouveau dossier application.");
  }
  const mealsAuthorityPatch = lot2MealsAuthorityPatch(workspace, lotId, mealsSource);
  if (!LOT_IDS.has(lotId)) throw new Error("Lot inconnu.");
  const current = workspace?.lots?.[lotId];
  if (!current || current.status !== "ready") {
    throw new Error("Importe d’abord ce lot avant de le corriger.");
  }
  const parsed = parseLotContent(content, contextWithWorkspace(context, workspace), lotId);
  const unchanged = JSON.stringify(stableValue(parsed)) === JSON.stringify(stableValue(current.content));
  if (unchanged) return workspace;
  const lot = CLIENT_APP_WORKSPACE_LOTS.find((item) => item.id === lotId);
  const sections = { ...workspace.sections };
  lot.sections.forEach((sectionId) => {
    sections[sectionId] = { ...sections[sectionId], status: "to_review", reviewed_at: null };
  });
  const lots = {
    ...workspace.lots,
    [lotId]: { ...current, content: parsed, edited_at: new Date().toISOString() },
  };
  revokeDependentLots(lots, sections, lotId);
  return touchWorkspace(workspace, {
    lots,
    sections,
    ...mealsAuthorityPatch,
  });
}

export function updateClientAppWorkspaceRawDraft(workspace, lotId, rawText) {
  if (!LOT_IDS.has(lotId) || typeof rawText !== "string" || rawText.length > 250000) {
    throw new Error("Le brouillon collé est invalide ou trop volumineux.");
  }
  if ((workspace?.raw_by_lot?.[lotId] || "") === rawText) return workspace;
  return touchWorkspace(workspace, {
    raw_by_lot: { ...(workspace?.raw_by_lot || {}), [lotId]: rawText },
  });
}

export function markClientAppWorkspaceSectionReviewed(workspace, sectionId, reviewed = true) {
  if (!SECTION_IDS.has(sectionId) || !workspace?.sections?.[sectionId]) {
    throw new Error("Section du dossier inconnue.");
  }
  const section = workspace.sections[sectionId];
  // Semaine relue sur les menus de l'éditeur : pas besoin du lot Menus et recettes.
  if (!workspace?.lots?.[section.lot_id] && !sectionCovered(workspace, { ...section, id: sectionId })) {
    throw new Error("Importe d’abord le lot correspondant.");
  }
  return touchWorkspace(workspace, {
    sections: {
      ...workspace.sections,
      [sectionId]: {
        ...section,
        status: reviewed ? "reviewed" : "to_review",
        reviewed_at: reviewed ? new Date().toISOString() : null,
      },
    },
  });
}

/**
 * Lots indispensables au dossier. Quand les menus de l'éditeur Semaine font
 * foi, le lot Menus et recettes n'est plus requis : introduction, stratégie,
 * réglages du Meal Prep, frigo et compléments viennent toujours des lots.
 */
export function requiredClientAppWorkspaceLots(workspace) {
  return workspace?.meals_authority?.source === MEALS_AUTHORITY_SOURCES.WEEK_EDITOR
    ? CLIENT_APP_WORKSPACE_LOTS.filter((lot) => lot.id !== "lot_2")
    : CLIENT_APP_WORKSPACE_LOTS;
}

function sectionCovered(workspace, section) {
  if (workspace?.lots?.[section.lot_id || section.lotId]?.status === "ready") return true;
  return section.id === "week" || section.lot_id === "lot_2" || section.lotId === "lot_2"
    ? workspace?.meals_authority?.source === MEALS_AUTHORITY_SOURCES.WEEK_EDITOR
    : false;
}

export function validateClientAppWorkspace(workspace, context) {
  const required = requiredClientAppWorkspaceLots(workspace);
  if (!workspace || workspace.version !== CLIENT_APP_WORKSPACE_VERSION
      || workspace.source_revision !== context?.sourceRevision
      || !workspace.lots || required.some((lot) => workspace.lots[lot.id]?.status !== "ready")
      || !workspace.sections) {
    throw new Error(required.length === CLIENT_APP_WORKSPACE_LOTS.length
      ? "Les quatre lots du dossier doivent être importés."
      : "Les lots Introduction et stratégie, Cycle et Meal Prep, Frigo et compléments doivent être importés.");
  }
  for (const section of CLIENT_APP_WORKSPACE_SECTIONS) {
    const state = workspace.sections[section.id];
    if (!state || !REVIEW_STATUSES.has(state.status) || state.status !== "reviewed") {
      throw new Error(`${section.label} doit être relue par Anissa.`);
    }
  }
  return true;
}

export function getClientAppWorkspacePublicationIssue({ client, consultation, directives = "" }) {
  let context;
  try {
    context = buildClientAppWorkspaceContext({ client, consultation, directives });
  } catch (error) {
    return error?.message || "Dossier application indisponible.";
  }
  const workspace = consultation?.editorial_overrides?.client_app_workspace;
  if (!workspace || workspace.version !== CLIENT_APP_WORKSPACE_VERSION) {
    return "Prépare et applique le Dossier application avant de publier.";
  }
  if (workspace.source_revision !== context.sourceRevision) {
    return "Le Dossier application doit être actualisé après les modifications du programme.";
  }
  if (workspace.status !== "applied") {
    return "Le Dossier application doit être entièrement relu puis appliqué à Livraison.";
  }
  if (!/^[0-9a-f]{64}$/.test(workspace.applied_content_sha256 || "")
      || workspace.applied_content_sha256 !== activeWorkspaceSha256(consultation)) {
    return "Le contenu de Livraison a changé depuis la relecture. Vérifie et applique à nouveau le Dossier application.";
  }
  try {
    validateClientAppWorkspace(workspace, context);
  } catch (error) {
    return error?.message || "Le Dossier application doit être relu par Anissa.";
  }
  return null;
}

export function buildAppliedConsultationFromWorkspace({ client, consultation, context, workspace }) {
  validateClientAppWorkspace(workspace, context);
  // Défense en profondeur : les contenus stockés sont revalidés au dernier
  // moment, y compris leur provenance, avant de construire le payload actif.
  // Source des menus : si Anissa a enregistré ses menus dans l'éditeur Semaine,
  // ce sont SES menus qui sont appliqués et le lot Menus et recettes n'est ni
  // requis ni lu. Une empreinte qui ne correspond plus signifie une
  // modification faite ailleurs : on refuse plutôt que d'écraser quoi que ce soit.
  const mealsAuthority = workspace.meals_authority;
  const useWeekEditor = mealsAuthority?.source === MEALS_AUTHORITY_SOURCES.WEEK_EDITOR;
  if (useWeekEditor && mealsAuthority.content_sha256 !== weekEditorContentSha256(consultation)) {
    throw new Error("Les menus ont changé depuis le dernier enregistrement de « Semaine, menus & recettes ». Ouvre cette section et enregistre-la avant d’appliquer.");
  }
  const lot1 = parseLot1(workspace.lots.lot_1.content);
  const lot2 = useWeekEditor ? null : parseLot2(workspace.lots.lot_2.content, context);
  const lot3 = parseLot3(workspace.lots.lot_3.content);
  const lot4 = parseLot4(
    workspace.lots.lot_4.content,
    contextWithWorkspace(context, useWeekEditor
      ? workspace
      : { ...workspace, lots: { ...workspace.lots, lot_2: { ...workspace.lots.lot_2, content: lot2 } } }),
  );
  if (lot2) assertNoExplicitFoodConflict(lot2.meals, context.packageContext.safetySnapshot);
  const baseOverrides = consultation.editorial_overrides || {};
  const withNarrative = {
    ...consultation,
    intro_letter: lot1.introduction,
    editorial_overrides: { ...baseOverrides, strategy: lot1.strategy },
  };
  const appliedMealsConsultation = useWeekEditor
    ? buildWeekEditorAppliedConsultation({
      client,
      consultation: withNarrative,
      context,
      mealPrepSettings: lot3.meal_prep,
    })
    : prepareClientAppPackageDraft({
      client,
      consultation: withNarrative,
      weekMeals: context.sourcePlan.sections.week_meals,
      packageText: JSON.stringify({
        schema: "anissa_client_app_package",
        version: 1,
        source_revision: context.packageContext.sourceRevision,
        meals: lot2.meals,
        meal_prep: lot3.meal_prep,
      }),
    }).nextConsultation;
  const nextOverrides = {
    ...appliedMealsConsultation.editorial_overrides,
    strategy: lot1.strategy,
    fridge: lot4.fridge,
    supplements_intro: lot4.supplements.intro,
  };
  delete nextOverrides.fridge_sync;
  const appliedAt = new Date().toISOString();
  const nextConsultationContent = {
    ...appliedMealsConsultation,
    intro_letter: lot1.introduction,
    editorial_overrides: {
      ...nextOverrides,
      client_app_package: {
        version: CLIENT_APP_WORKSPACE_VERSION,
        status: "validated",
        source_revision: context.sourceRevision,
        content_sha256: sha256HexSync(JSON.stringify(stableValue(workspace.lots))),
        accepted_at: appliedAt,
      },
    },
  };
  const appliedWorkspace = {
    ...workspace,
    status: "applied",
    draft_revision: Number(workspace.draft_revision || 0) + 1,
    applied_content_sha256: activeWorkspaceSha256(nextConsultationContent),
    applied_meals_sha256: weekEditorContentSha256(nextConsultationContent),
    applied_at: appliedAt,
    updated_at: appliedAt,
    ...(useWeekEditor ? {
      meals_authority: {
        ...mealsAuthority,
        content_sha256: weekEditorContentSha256(nextConsultationContent),
        updated_at: appliedAt,
      },
    } : {}),
  };
  const nextConsultation = {
    ...nextConsultationContent,
    editorial_overrides: {
      ...nextConsultationContent.editorial_overrides,
      client_app_workspace: appliedWorkspace,
    },
  };
  buildClientAppPlanFromConsultation(client, nextConsultation);
  return nextConsultation;
}

export async function persistClientAppWorkspaceDraft({ client, consultation, context, workspace, save }) {
  if (typeof save !== "function" || workspace?.source_revision !== context?.sourceRevision) {
    throw new Error("Le brouillon ne correspond plus à la version actuelle du programme.");
  }
  const fresh = buildClientAppWorkspaceContext({
    client,
    consultation,
    directives: context.directives || "",
  });
  if (fresh.sourceRevision !== context.sourceRevision) {
    throw new Error("Le programme a changé. Le brouillon n’a pas été enregistré.");
  }
  return save({
    ...consultation,
    editorial_overrides: {
      ...(consultation.editorial_overrides || {}),
      client_app_workspace: workspace,
    },
  });
}

export async function applyClientAppWorkspace({ client, consultation, context, workspace, save }) {
  if (typeof save !== "function") throw new Error("L’enregistrement est indisponible.");
  const fresh = buildClientAppWorkspaceContext({
    client,
    consultation,
    directives: context?.directives || "",
  });
  if (fresh.sourceRevision !== context?.sourceRevision) {
    throw new Error("Le programme a changé. Vérifie les lots avant de les appliquer.");
  }
  const next = buildAppliedConsultationFromWorkspace({ client, consultation, context: fresh, workspace });
  return save(next);
}

// Les limites doivent voyager avec le cadre : sans elles, la réponse préparée
// dépasse les bornes et Anissa enchaîne les refus sans savoir pourquoi.
function lotFormatRules(lotId) {
  const heading = "CONTRAINTES DE FORME — un seul écart et le lot est refusé";
  const common = [
    "- Aucune clé supplémentaire, aucune clé manquante, à aucun niveau.",
    "- Deux entrées d’une même liste ne peuvent pas être identiques, même avec une casse différente.",
    "- Les retours à la ligne à l’intérieur d’un texte sont remplacés par une espace : n’en utilise pas pour structurer un paragraphe.",
  ];
  if (lotId === "lot_1") {
    const limits = LOT_1_LIMITS;
    return [
      heading,
      `- introduction.greeting : texte non vide, ${limits.greeting} caractères au maximum.`,
      `- introduction.body : ${limits.body.min} à ${limits.body.max} paragraphes, ${limits.body.maxLength} caractères chacun au maximum, tous différents.`,
      `- introduction.pull_quote : ${limits.pull_quote} caractères au maximum. La chaîne vide est acceptée, mais la clé reste obligatoire.`,
      `- introduction.tailored_points : ${limits.tailored_points.min} à ${limits.tailored_points.max} entrées, chacune exactement {title, detail}, title ${limits.tailored_points.title} caractères au maximum, detail ${limits.tailored_points.detail}.`,
      `- introduction.signature : texte non vide, ${limits.signature} caractères au maximum.`,
      `- strategy.subtitle : texte non vide, ${limits.subtitle} caractères au maximum.`,
      `- strategy.essential : ${limits.essential.min} à ${limits.essential.max} entrées, ${limits.essential.maxLength} caractères chacune au maximum, toutes différentes.`,
      `- strategy.pillars : ${limits.pillars.min} à ${limits.pillars.max} entrées, chacune exactement {title, description}, title ${limits.pillars.title} caractères au maximum, description ${limits.pillars.description}.`,
      `- strategy.takeaways : ${limits.takeaways.min} à ${limits.takeaways.max} entrées, ${limits.takeaways.maxLength} caractères chacune au maximum, toutes différentes.`,
      ...common,
    ].join("\n");
  }
  if (lotId === "lot_2") {
    return [
      heading,
      "- Chaque repas garde le slot et la structure fournis ci-dessus, sans en ajouter ni en retirer.",
      "- Pour chaque repas : le plat principal, puis exactement 4 alternatives. Si le programme ne les détaille pas, tu les proposes, cohérentes avec son intention, ses exclusions et ses restrictions.",
      "- Chaque recette porte des ingrédients structurés avec une quantité numérique et une unité visibles. Une quantité proposée reste une proposition relue par Anissa : elle doit être plausible et cohérente, jamais laissée vide.",
      "- Les 4 alternatives d'un même repas doivent être réellement différentes entre elles : ce sont elles qui font les semaines 2, 3 et 4 de la rotation.",
      "- grammages_provenance est à recopier exactement, sans reformulation.",
      ...common,
    ].join("\n");
  }
  if (lotId === "lot_3") {
    return [
      heading,
      "- rotation.mode : exactement \"four_distinct_weeks\".",
      "- meal_prep.people : nombre entier de 1 à 20.",
      "- meal_prep.session_count : nombre entier de 1 à 3.",
      "- meal_prep.preferred_days : exactement autant de jours que session_count, tous différents, parmi monday, tuesday, wednesday, thursday, friday, saturday, sunday.",
      ...common,
    ].join("\n");
  }
  return [
    heading,
    "- fridge.to_favor : au moins une entrée, reprise mot pour mot dans les repères autorisés ci-dessus.",
    "- fridge.to_limit : zéro entrée ou plus, dans les mêmes repères autorisés.",
    "- supplements.intro et supplements.source_items_sha256 : à recopier exactement, sans reformulation ni ajout.",
    ...common,
  ].join("\n");
}

export { lotFormatRules as clientAppWorkspaceLotFormatRules };

/**
 * Enveloppe exacte attendue pour un lot, pré-remplie comme le cadre de
 * préparation (mêmes valeurs par défaut). Sert au cadre copié et au
 * pré-remplissage des lots 3 et 4 dans le cockpit.
 */
export function buildClientAppWorkspaceLotTemplate(context, lotId, workspace = null) {
  if (!LOT_IDS.has(lotId) || !context?.sourceRevision) throw new Error("Lot indisponible.");
  return lotTemplate(contextWithWorkspace(context, workspace), lotId);
}

function lotTemplate(effectiveContext, lotId) {
  const common = {
    schema: CLIENT_APP_WORKSPACE_SCHEMA,
    version: CLIENT_APP_WORKSPACE_VERSION,
    source_revision: effectiveContext.sourceRevision,
    lot_id: lotId,
    end_marker: effectiveContext.endMarkers[lotId],
    status: "ready",
    issues: [],
  };
  let content;
  if (lotId === "lot_1") {
    content = {
      introduction: { greeting: "Bonjour,", body: ["Paragraphe complet"], pull_quote: "", tailored_points: [], signature: "Anissa" },
      strategy: { subtitle: "Sous-titre", essential: ["Repère essentiel"], pillars: [{ title: "Pilier", description: "Description" }], takeaways: ["À retenir"] },
    };
  } else if (lotId === "lot_2") {
    const recipeTemplate = (label) => ({
      ingredients_structured: [{
        foodKey: "aliment",
        label,
        quantity: 100,
        unit: "g",
        preparation: "",
        category: "other",
      }],
      preparation: ["Étape complète"],
      prep_time_min: 0,
      cook_time_min: 0,
      servings: 1,
      tip: "",
    });
    content = { meals: effectiveContext.packageContext.meals.map((meal) => ({
      slot: meal.slot,
      primary: {
        title: meal.title,
        hint: meal.hint || "Résumé chiffré",
        recipe: recipeTemplate("Aliment principal"),
      },
      alternatives: Array.from({ length: 4 }, (_, index) => ({
        title: `Menu alternatif ${index + 1}`,
        hint: "Résumé chiffré",
        recipe: recipeTemplate(`Aliment alternatif ${index + 1}`),
      })),
    })), grammages_provenance: {
      source_revision: effectiveContext.packageContext.sourceRevision,
      status: "proposed_for_anissa_review",
    } };
  } else if (lotId === "lot_3") {
    content = { rotation: { mode: "four_distinct_weeks" }, meal_prep: { people: 1, session_count: 2, preferred_days: ["sunday", "wednesday"] } };
  } else {
    content = { fridge: { to_favor: [...effectiveContext.fridgeCandidates], to_limit: [] }, supplements: { intro: effectiveContext.supplementItems.length ? "Retrouve ici les compléments et les moments de prise déjà validés avec Anissa." : "Aucun complément prévu dans ce programme.", source_items_sha256: effectiveContext.supplementItemsSha256 } };
  }
  return { ...common, content };
}

export function buildClientAppWorkspaceLotPrompt(context, lotId, workspace = null) {
  if (!LOT_IDS.has(lotId) || !context?.sourceRevision) throw new Error("Lot indisponible.");
  const effectiveContext = contextWithWorkspace(context, workspace);
  const template = lotTemplate(effectiveContext, lotId);
  return [
    `DOSSIER APPLICATION — ${CLIENT_APP_WORKSPACE_LOTS.find((lot) => lot.id === lotId).label.toUpperCase()}`,
    "Prépare uniquement le lot demandé à partir du programme et des éléments structurés ci-dessous.",
    // La ligne rouge est sur le clinique, pas sur le culinaire. Anissa relit
    // chaque section et applique elle-même : une proposition de repas lui fait
    // gagner du temps, une invention clinique lui ferait courir un risque.
    "INTERDIT, sans exception : ajouter ou modifier un complément, un dosage, une posologie, un diagnostic, une durée de cure ou toute affirmation de santé. Introduire un aliment exclu, allergène ou contraire aux restrictions relues. Modifier la structure des repas, leurs créneaux, ou une décision déjà prise dans le programme.",
    "ATTENDU : tu proposes le contenu culinaire manquant, menus, variantes, ingrédients et quantités, dans le respect strict des interdictions ci-dessus. Ces propositions sont enregistrées comme telles et relues par Anissa avant toute application. Ne renvoie donc pas un refus au motif que le programme ne détaille pas déjà les quantités ou les alternatives : les proposer est précisément ce qui t’est demandé.",
    "Ne retourne status=needs_anissa_validation, content=null et des issues précises que si le lot touche une interdiction ci-dessus, si le programme est contradictoire, ou s’il est trop incomplet pour proposer sans trahir son intention.",
    "Réponds uniquement avec l’objet JSON exact, sans markdown ni commentaire.",
    NO_SELF_MENTION_INSTRUCTION,
    `Le champ end_marker doit rester exactement : ${effectiveContext.endMarkers[lotId]}`,
    lotId === "lot_2" ? `Structure des repas et contraintes relues :\n${JSON.stringify({ meals: effectiveContext.packageContext.meals, safety: effectiveContext.packageContext.safetySnapshot }, null, 2)}` : "",
    lotId === "lot_4" ? `Repères autorisés :\n${JSON.stringify({ fridge_candidates: effectiveContext.fridgeCandidates, supplement_items: effectiveContext.supplementItems }, null, 2)}` : "",
    lotId === "lot_1" ? [
      `PROGRAMME AUTORITATIF — LONGUEUR ${effectiveContext.narrativeSourceLength} CARACTÈRES — SHA-256 ${effectiveContext.narrativeSourceSha256}`,
      effectiveContext.narrativeSource,
      effectiveContext.narrativeSourceEndMarker,
    ].join("\n") : "",
    lotId === "lot_1"
      ? `Contrôle obligatoire : le programme autoritatif doit contenir exactement ${effectiveContext.narrativeSourceLength} caractères avant le marqueur, avoir l’empreinte SHA-256 ${effectiveContext.narrativeSourceSha256} et se terminer par ${effectiveContext.narrativeSourceEndMarker}. Si le marqueur est absent, si la longueur ou l’empreinte est incohérente, ou si une phrase paraît coupée, retourne status=needs_anissa_validation, content=null et décris précisément le problème dans issues.`
      : "",
    effectiveContext.directives ? `DIRECTIVES RELUES\n${effectiveContext.directives}` : "",
    lotFormatRules(lotId),
    "FORMAT EXACT À REMPLIR",
    JSON.stringify(template, null, 2),
  ].filter(Boolean).join("\n\n");
}
